import { useCallback, useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { FactCheckResult } from "@/api/fact-check-api"
import type { ChatAppResponse, ChatAppResponseOrError } from "@/api/models"
import type { HtmlParsedAnswer } from "@/features/chat/components/answer-parser"

import { submitQuickSentiment } from "@/api/feedback-api"
import { FeedbackDialog } from "@/components/feedback-dialog"
import {
  BookmarkPlus,
  Check,
  ClipboardCopy,
  Copy,
  FilePlus2,
  ThumbsDown,
  ThumbsUp,
} from "@/components/icons"
import { buttonVariants } from "@/components/ui/button-variants"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { isPersonalSourcesContext } from "@/features/ai-explorer/utils/personal-search-utils"
import { DownloadDocxMenu } from "@/features/chat/components/download-docx-menu"
import { FactCheckButton } from "@/features/chat/components/fact-check-button"
import { useConfigQuery } from "@/hooks/use-config-query"
import { cn } from "@/lib/utils"
import { getResponseContent, getResponseContext } from "@/utils/response-format-utils"

type CopyAnswerMenuProps = {
  disabled: boolean
  showRawOption: boolean
  onCopyFormatted: () => void
  onCopyRaw: () => void
  tooltip: string
  formattedLabel: string
  rawLabel: string
}

function CopyAnswerMenu({
  disabled,
  showRawOption,
  onCopyFormatted,
  onCopyRaw,
  tooltip,
  formattedLabel,
  rawLabel,
}: CopyAnswerMenuProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const copiedLabel = t("system_message.copied", { defaultValue: "Copied!" })

  const handleCopy = useCallback((fn: () => void) => {
    fn()
    setCopied(true)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setCopied(false), 1500)
  }, [])

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const CopyIcon = copied ? Check : Copy

  if (!showRawOption) {
    return (
      <Tooltip>
        <TooltipTrigger
          aria-label={tooltip}
          disabled={disabled}
          className={cn(
            buttonVariants({
              size: "icon",
              variant: "icon",
              className: "size-7",
            }),
            copied && "text-primary"
          )}
          onClick={() => handleCopy(onCopyFormatted)}
        >
          <CopyIcon className="size-5 transition-transform duration-150" />
        </TooltipTrigger>
        <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
          <p>{copied ? copiedLabel : tooltip}</p>
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger
            aria-label={tooltip}
            disabled={disabled}
            className={cn(
              buttonVariants({
                size: "icon",
                variant: "icon",
                className: "size-7",
              }),
              copied && "text-primary"
            )}
          >
            <CopyIcon className="size-5 transition-transform duration-150" />
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
          <p>{copied ? copiedLabel : tooltip}</p>
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end">
        <DropdownMenuItem className="cursor-pointer" onClick={() => handleCopy(onCopyFormatted)}>
          <Copy className="mr-2 h-4 w-4" />
          <span>{formattedLabel}</span>
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer" onClick={() => handleCopy(onCopyRaw)}>
          <ClipboardCopy className="mr-2 h-4 w-4" />
          <span>{rawLabel}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

type SystemMessageActionsProps = {
  answer: ChatAppResponse | ChatAppResponseOrError
  citationSection?: string
  onCopyToClipboardClicked: () => void
  onCopyRawToClipboardClicked: () => void
  onDownloadClicked: (useTemplate: boolean) => void
  onCreateDocumentClicked?: () => void
  parsedAnswer: HtmlParsedAnswer
  showCopyRaw: boolean
  /** When false, no copy control is rendered. Hiding only the raw option still copies the formatted answer. */
  showCopy?: boolean
  showDownload: boolean
  showFactCheck?: boolean
  targetLanguage?: string
  userQuery?: string
  showCreateDocument?: boolean
  /** HERMES B4: "Publish as artifact" (hosted-agent threads only, writers only). */
  showPublishArtifact?: boolean
  onPublishArtifactClicked?: () => void
  sessionType: string
  /** Session this answer belongs to. Sent with thumbs feedback. */
  sessionId?: string
}

type SentimentState = "idle" | "up" | "down"

export function SystemMessageActions({
  answer,
  onCopyRawToClipboardClicked,
  onCopyToClipboardClicked,
  onDownloadClicked,
  onCreateDocumentClicked,
  parsedAnswer,
  showCopyRaw,
  showCopy = true,
  showDownload,
  showFactCheck = true,
  targetLanguage,
  userQuery,
  showCreateDocument = false,
  showPublishArtifact = false,
  onPublishArtifactClicked,
  sessionType,
  sessionId,
}: SystemMessageActionsProps) {
  const { t } = useTranslation()
  const { data: config } = useConfigQuery()

  // Check if template is available from config API
  // Config includes docxTemplateEnabled flag set by backend when DOCX_TEMPLATE_PATH is configured
  const hasTemplate = Boolean(config?.docxTemplateEnabled ?? false)

  // Thumbs feedback state: "up" → submitted positive, "down" → dialog opened
  // (or feedback already filed). "idle" → not yet interacted. Local-only —
  // resets if the message component unmounts; that's fine, the record is on
  // the server.
  const [sentiment, setSentiment] = useState<SentimentState>("idle")
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false)
  // A personal-sources answer is never copied into a feedback record (operators see counts only).
  const responseContent = isPersonalSourcesContext(getResponseContext(answer))
    ? ""
    : getResponseContent(answer)
  const messageId = getResponseContext(answer).message_id
  const ctxSessionId =
    (getResponseContext(answer) as unknown as { session_id?: string }).session_id ?? sessionId
  const canSendFeedback = parsedAnswer.answerHtml.length > 0 && Boolean(messageId)

  const handleThumbsUp = useCallback(async () => {
    if (sentiment === "up") return
    setSentiment("up")
    try {
      await submitQuickSentiment({
        sentiment: "up",
        messageId: messageId ?? undefined,
        sessionId: ctxSessionId ?? undefined,
        responseContent,
      })
      toast.success(t("feedback.toast.thumbsUp", "Thanks for the feedback!"))
    } catch {
      // Roll back so the user can retry.
      setSentiment("idle")
      toast.error(t("feedback.toast.thumbsUpFailed", "Could not record feedback. Try again?"))
    }
  }, [ctxSessionId, messageId, responseContent, sentiment, t])

  const handleThumbsDown = useCallback(() => {
    setSentiment("down")
    setFeedbackDialogOpen(true)
  }, [])

  return (
    <>
      <Tooltip>
        <TooltipTrigger
          aria-label={t("feedback.tooltip.thumbsUp", "Helpful answer")}
          aria-pressed={sentiment === "up"}
          disabled={!canSendFeedback}
          className={cn(
            buttonVariants({ size: "icon", variant: "icon", className: "size-7" }),
            sentiment === "up" && "text-primary"
          )}
          onClick={handleThumbsUp}
        >
          <ThumbsUp className="size-5 transition-transform duration-150" />
        </TooltipTrigger>
        <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
          <p>
            {sentiment === "up"
              ? t("feedback.toast.thumbsUp", "Thanks for the feedback!")
              : t("feedback.tooltip.thumbsUp", "Helpful answer")}
          </p>
        </TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger
          aria-label={t("feedback.tooltip.thumbsDown", "Not helpful — tell us why")}
          aria-pressed={sentiment === "down"}
          disabled={!canSendFeedback}
          className={cn(
            buttonVariants({ size: "icon", variant: "icon", className: "size-7" }),
            sentiment === "down" && "text-primary"
          )}
          onClick={handleThumbsDown}
        >
          <ThumbsDown className="size-5 transition-transform duration-150" />
        </TooltipTrigger>
        <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
          <p>{t("feedback.tooltip.thumbsDown", "Not helpful — tell us why")}</p>
        </TooltipContent>
      </Tooltip>
      <FeedbackDialog
        hideTrigger
        lockLane
        initialLane="response_feedback"
        initialSentiment="down"
        open={feedbackDialogOpen}
        prefillContext={{
          messageId: messageId ?? null,
          sessionId: ctxSessionId ?? null,
          responseContent,
        }}
        onOpenChange={setFeedbackDialogOpen}
      />
      {showCopy && (
        <CopyAnswerMenu
          disabled={parsedAnswer.answerHtml.length < 1}
          formattedLabel={t("answer.copy_formatted", { defaultValue: "Copy formatted" })}
          rawLabel={t("answer.copy_raw", { defaultValue: "Copy raw Markdown" })}
          showRawOption={showCopyRaw}
          tooltip={t("answer.copy_answer")}
          onCopyFormatted={onCopyToClipboardClicked}
          onCopyRaw={onCopyRawToClipboardClicked}
        />
      )}
      {showDownload && (
        <DownloadDocxMenu
          disabled={parsedAnswer.answerHtml.length < 1}
          hasTemplate={hasTemplate}
          onDownload={onDownloadClicked}
        />
      )}
      {showCreateDocument && (
        <Tooltip>
          <TooltipTrigger
            aria-label={t("answer.create_document", { defaultValue: "Create Document" })}
            disabled={parsedAnswer.answerHtml.length < 1}
            className={cn(
              buttonVariants({
                size: "icon",
                variant: "icon",
                className: "size-7",
              })
            )}
            onClick={() => onCreateDocumentClicked?.()}
          >
            <FilePlus2 className="size-5" />
          </TooltipTrigger>
          <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
            <p>{t("answer.create_document", { defaultValue: "Create Document" })}</p>
          </TooltipContent>
        </Tooltip>
      )}
      {showPublishArtifact && (
        <Tooltip>
          <TooltipTrigger
            aria-label={t("answer.publish_artifact")}
            data-testid="publish-artifact-button"
            disabled={parsedAnswer.answerHtml.length < 1}
            className={cn(
              buttonVariants({
                size: "icon",
                variant: "icon",
                className: "size-7",
              })
            )}
            onClick={() => onPublishArtifactClicked?.()}
          >
            <BookmarkPlus className="size-5" />
          </TooltipTrigger>
          <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
            <p>{t("answer.publish_artifact")}</p>
          </TooltipContent>
        </Tooltip>
      )}
      {showFactCheck && getResponseContent(answer) && (
        <FactCheckButton
          answerText={getResponseContent(answer)}
          language={targetLanguage}
          messageId={getResponseContext(answer).message_id}
          sessionType={sessionType}
          userQuery={userQuery}
          citations={parsedAnswer.citations.map((citation, index) => {
            const isUrl = citation.startsWith("http://") || citation.startsWith("https://")
            return {
              id: `citation-${index}`,
              title: citation,
              kind: isUrl ? ("url" as const) : ("doc" as const),
            }
          })}
          initialResult={
            getResponseContext(answer).fact_check_result
              ? (getResponseContext(answer).fact_check_result as unknown as FactCheckResult)
              : null
          }
        />
      )}
    </>
  )
}
